import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMemberCards } from '../services/api';
import { askAssistant, HAS_AI_KEY } from '../services/ai';

const SUGGESTED = [
  { icon: '📋', text: 'What do I need for my jobs tomorrow?' },
  { icon: '📍', text: 'Where are my job sites?' },
  { icon: '👷', text: 'Who else is on my crew?' },
  { icon: '✅', text: "What's left to do on my jobs?" },
  { icon: '📝', text: 'Any notes from Ryan on my jobs?' },
  { icon: '⚡', text: 'What materials do I need?' },
];

// Simple markdown-ish renderer for bold and bullets
function MessageText({ text }) {
  const lines = text.split('\n');
  return (
    <div className="msg-text">
      {lines.map((line, i) => {
        if (!line.trim()) return <br key={i} />;
        // Bold **text**
        const parts = line.split(/\*\*(.*?)\*\*/g);
        const rendered = parts.map((part, j) =>
          j % 2 === 1 ? <strong key={j}>{part}</strong> : part
        );
        // Bullet point
        if (line.trimStart().startsWith('•') || line.trimStart().startsWith('-')) {
          return <div key={i} className="msg-bullet">{rendered}</div>;
        }
        // Emoji prefix lines (job headers)
        if (/^[📍📋👷✅📝⚡🔧]/.test(line.trim())) {
          return <div key={i} className="msg-job-header">{rendered}</div>;
        }
        return <div key={i}>{rendered}</div>;
      })}
    </div>
  );
}

export default function AskAI() {
  const { worker, activeBoard } = useAuth();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(true);
  const [listening, setListening] = useState(false);
  const bottomRef = useRef();
  const inputRef = useRef();
  const recRef = useRef();

  const convKey = `titan_askai_${worker?.id || worker?.fullName || 'me'}`;

  useEffect(() => {
    if (!worker) { navigate('/'); return; }
    // Restore the prior conversation so leaving and coming back doesn't start over.
    let restored = false;
    try {
      const raw = localStorage.getItem(convKey);
      if (raw) { const saved = JSON.parse(raw); if (Array.isArray(saved) && saved.length) { setMessages(saved); restored = true; } }
    } catch {}
    loadContext(restored);
  }, [worker]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Persist the conversation on this device as it grows.
  useEffect(() => {
    if (messages.length) { try { localStorage.setItem(convKey, JSON.stringify(messages)); } catch {} }
  }, [messages, convKey]);

  async function loadContext(keepMessages) {
    setContextLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
      function getToken() {
        try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; }
      }
      function apiFetch(path) {
        const token = getToken();
        return fetch(`${API_BASE}${path}`, {
          headers: { 'Content-Type': 'application/json', ...(token ? { 'x-worker-token': token } : {}) },
        }).then(r => r.ok ? r.json() : Promise.reject());
      }

      const [allCards, myProjects, checklists] = await Promise.all([
        getMemberCards(),
        apiFetch('/my-projects').catch(() => []),
        apiFetch('/checklists').catch(() => []),
      ]);

      // Load conversations for each assigned project (in parallel, ignore failures)
      const convResults = await Promise.all(
        (myProjects || []).map(p =>
          apiFetch(`/projects/${p.id}/conversations`).catch(() => [])
        )
      );

      // Each convResult[i] is [{name: subJobName, notes: [{note, created_at, first_name, last_name}]}]
      const conversations = [];
      (myProjects || []).forEach((p, i) => {
        const threads = convResults[i] || [];
        threads.forEach(thread => {
          conversations.push({ projectName: p.name, subJobName: thread.name, notes: thread.notes || [] });
        });
      });

      const firstName = worker.firstName || worker.fullName?.split(' ')[0] || 'there';
      const fullName  = worker.fullName || `${worker.firstName || ''} ${worker.lastName || ''}`.trim();
      const ctx = {
        cards: allCards,
        workerName: fullName,
        boardName: 'Titan Electric',
        conversations,
        checklists: Array.isArray(checklists) ? checklists : [],
        hoursThisWeek: 0,
        timesheets: [],
      };
      setContext(ctx);
      const jobCount = allCards.length;
      const noteCount = conversations.reduce((sum, t) => sum + (t.notes?.length || 0), 0);
      if (!keepMessages) setMessages([{
        role: 'ai',
        text: jobCount > 0
          ? `Hey ${firstName}! I've loaded your ${jobCount} job${jobCount > 1 ? 's' : ''}${noteCount > 0 ? ` and ${noteCount} daily note${noteCount !== 1 ? 's' : ''} from your projects` : ''}. Ask me anything — what you need, where to be, what's left to do, or what's been discussed on your jobs.`
          : `Hey ${firstName}! I don't see any jobs assigned to you right now. Check with Ryan if you're expecting work.`,
      }]);
    } catch (e) {
      if (!keepMessages) setMessages([{ role: 'ai', text: 'Had trouble loading your jobs. Try going back and refreshing.' }]);
    } finally {
      setContextLoading(false);
    }
  }

  async function send(text) {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');

    const userMsg = { role: 'user', text: msg };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      // Pass conversation history (excluding greeting) for multi-turn context
      const history = messages.filter(m => m.role !== 'system');
      const reply = await askAssistant(msg, context || { cards: [], workerName: worker.fullName, boardName: '' }, history);
      setMessages(prev => [...prev, { role: 'ai', text: reply }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'ai', text: `Sorry, I ran into an issue: ${e.message}. Try again in a moment.` }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }

  function newChat() {
    if (messages.length > 1 && !window.confirm('Start a new chat? This clears the current conversation.')) return;
    try { localStorage.removeItem(convKey); } catch {}
    setMessages([]);
    loadContext(false);
  }

  function startVoice() {
    const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRec) { alert('Voice input not supported on this browser.'); return; }
    const rec = new SpeechRec();
    rec.onresult = e => {
      const transcript = Array.from(e.results).map(r => r[0].transcript).join(' ');
      setInput(prev => (prev ? prev + ' ' : '') + transcript);
      setListening(false);
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recRef.current = rec;
    rec.start();
    setListening(true);
  }

  const showSuggestions = messages.length <= 1 && !loading;

  return (
    <div className="page chat-page">
      {/* Top bar */}
      <div className="top-bar">
        <button className="back-btn-inline" onClick={() => navigate('/dashboard')}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M11 4L6 9l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div style={{ flex: 1 }}>
          <div className="top-bar-title">Ask AI</div>
          <div className="top-bar-sub">
            {contextLoading ? 'Loading your jobs…' : context
              ? `${context.cards.length} job${context.cards.length !== 1 ? 's' : ''} loaded · ${HAS_AI_KEY ? 'Live AI' : 'Demo mode'}`
              : 'Ready'}
          </div>
        </div>
        <button onClick={newChat} title="Start a new chat"
          style={{ background: 'none', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: '5px 10px', borderRadius: 16, whiteSpace: 'nowrap', marginRight: 8 }}>
          + New
        </button>
        <div className={`ai-pill ${HAS_AI_KEY ? 'ai-pill-live' : 'ai-pill-demo'}`}>
          <span className="ai-pill-dot" />
          {HAS_AI_KEY ? 'Live' : 'Demo'}
        </div>
      </div>

      {/* Chat messages */}
      <div className="chat-body">
        {contextLoading && (
          <div className="chat-loading">
            <div className="spinner" />
            <span>Loading your job data…</span>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`bubble bubble-${m.role}`}>
            {m.role === 'ai' && (
              <div className="bubble-ai-label">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <circle cx="6" cy="6" r="5" stroke="var(--orange)" strokeWidth="1.2" fill="none"/>
                  <path d="M4 6h4M6 4v4" stroke="var(--orange)" strokeWidth="1.2" strokeLinecap="round"/>
                </svg>
                Titan AI
              </div>
            )}
            <MessageText text={m.text} />
          </div>
        ))}

        {loading && (
          <div className="bubble bubble-ai">
            <div className="bubble-ai-label">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <circle cx="6" cy="6" r="5" stroke="var(--orange)" strokeWidth="1.2" fill="none"/>
              </svg>
              Titan AI
            </div>
            <div className="bubble-thinking">
              <span className="dot" /><span className="dot" /><span className="dot" />
            </div>
          </div>
        )}

        {/* Quick-tap suggestions */}
        {showSuggestions && !contextLoading && (
          <div className="suggestions-grid">
            {SUGGESTED.map(s => (
              <button key={s.text} className="suggestion-chip" onClick={() => send(s.text)}>
                <span className="suggestion-icon">{s.icon}</span>
                {s.text}
              </button>
            ))}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input bar */}
      <div className="chat-input-bar">
        <button
          className={`mic-btn ${listening ? 'mic-active' : ''}`}
          onClick={listening ? () => { recRef.current?.stop(); setListening(false); } : startVoice}
          aria-label="Voice input"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <rect x="6" y="1" width="6" height="9" rx="3" stroke="currentColor" strokeWidth="1.4" fill={listening ? 'currentColor' : 'none'}/>
            <path d="M2 9c0 3.9 3.1 7 7 7s7-3.1 7-7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" fill="none"/>
            <path d="M9 16v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        </button>
        <input
          ref={inputRef}
          className="chat-input"
          placeholder={contextLoading ? 'Loading jobs…' : 'Ask about your jobs…'}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
          disabled={loading || contextLoading}
        />
        <button
          className="send-btn"
          onClick={() => send()}
          disabled={!input.trim() || loading || contextLoading}
          aria-label="Send"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M2 9h14M10 3l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
  );
}
